
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { getAllPosts, getAllLessons } from "@/lib/contentQueries";
import { isSafeContentSegment } from "@/lib/contentRouteSafety";
import Head from "next/head";
import { useTina } from "tinacms/dist/react";
import BlogPost from "../../components/blog/BlogPost";
import LessonOpeningReference from "../../components/blog/LessonOpeningReference";
import client from "../../../tina/__generated__/client";
function getMetaText(value: unknown): string {
    if (typeof value === "string")
        return value;
    if (Array.isArray(value))
        return value.map(getMetaText).join(" ");
    if (!value || typeof value !== "object")
        return "";
    const node = value as {
        text?: unknown;
        children?: unknown[];
    };
    if (typeof node.text === "string")
        return node.text;
    return Array.isArray(node.children)
        ? node.children.map(getMetaText).join(" ")
        : "";
}
function PostPage(props) {
    const { data } = useTina({
        query: props.query,
        variables: props.variables,
        data: props.data,
    });
    return data?.post ? <BlogPost {...data.post}/> : null;
}
function LessonPage(props) {
    const { data } = useTina({
        query: props.query,
        variables: props.variables,
        data: props.data,
    });
    return data?.lesson ? <LessonOpeningReference lesson={data.lesson}/> : null;
}
export default function OpeningPage(props) {
    const content = props.source === "post" ? props.data?.post : props.data?.lesson;
    const name = props.source === "post"
        ? content?.opening?.name || content?.title
        : content?.lesson;
    const isOpeningReference = props.source === "lesson" || content?.template === "opening-reference";
    const title = isOpeningReference
        ? name
            ? `${name} Chess Opening – Moves and Ideas | Chess Hotel`
            : "Chess Opening Guide | Chess Hotel"
        : `${content?.title || "Chess Guide"} | Chess Hotel`;
    const fallbackDescription = `Learn the key moves, ideas, plans, and common variations of the ${name || "featured"} chess opening.`;
    const description = [content?.excerpt, content?.description, content?.subtitle]
        .map(getMetaText)
        .find((value) => value.trim())
        ?.replace(/\s+/g, " ")
        .trim() || fallbackDescription;
    const canonicalUrl = `https://www.chesshotel.com/openings/${content?.slug || props.variables?.relativePath?.replace(/^openings\//, "").replace(/\.mdx$/, "") || ""}`;
    const imageUrl = content?.heroImg
        ? new URL(content.heroImg, "https://www.chesshotel.com").toString()
        : undefined;
    const articleSchema = props.source === "post"
        ? {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: content?.title,
            description,
            ...(imageUrl ? { image: [imageUrl] } : {}),
            ...(content?.date
                ? { datePublished: content.date, dateModified: content.date }
                : {}),
            ...(content?.author?.name
                ? {
                    author: {
                        "@type": "Person",
                        name: content.author.name,
                    },
                }
                : {}),
            publisher: {
                "@type": "Organization",
                name: "Chess Hotel",
                url: "https://www.chesshotel.com",
            },
            mainEntityOfPage: canonicalUrl,
        }
        : null;
    return (<>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description}/>
        <meta property="og:title" content={title}/>
        <meta property="og:description" content={description}/>
        <meta property="og:type" content="article"/>
        <meta property="og:url" content={canonicalUrl}/>
        {imageUrl && (<meta property="og:image" content={imageUrl} key="og-image"/>)}
        <meta name="twitter:card" content="summary_large_image"/>
        <meta name="twitter:title" content={title}/>
        <meta name="twitter:description" content={description}/>
        {imageUrl && <meta name="twitter:image" content={imageUrl}/>}
        {articleSchema && (<script type="application/ld+json" dangerouslySetInnerHTML={{
                __html: JSON.stringify(articleSchema).replace(/</g, "\\u003c"),
            }}/>)}
      </Head>
      {props.source === "post" ? (<PostPage {...props}/>) : (<LessonPage {...props}/>)}
    </>);
}
export const getStaticPaths = async () => {
    const [postConnection, lessonConnection] = await Promise.all([
        getAllPosts(),
        getAllLessons(),
    ]);
    const slugs = new Set<string>();
    postConnection.data.postConnection.edges.forEach((edge) => {
        const [topic, slug] = edge.node._sys.breadcrumbs;
        if (topic === "openings" && slug)
            slugs.add(slug);
    });
    lessonConnection.data.lessonConnection.edges.forEach((edge) => {
        if (edge.node.slug)
            slugs.add(edge.node.slug);
    });
    return {
        paths: Array.from(slugs).map((slug) => ({
            params: { slug },
            locale: "en",
        })),
        fallback: process.env.CONTENT_SNAPSHOT_BUILD !== "true" ? "blocking" : false,
    };
};
const getStaticProps = async ({ params }) => {
    const { slug } = params;
    if (!isSafeContentSegment(slug)) {
        return { notFound: true };
    }
    try {
        const post = await client.queries.post({
            relativePath: `openings/${slug}.mdx`,
        });
        if (post?.data?.post) {
            return { props: { source: "post", ...post } };
        }
    }
    catch {
        // A lesson can provide a lightweight reference until its article exists.
    }
    try {
        const lesson = await client.queries.lesson({
            relativePath: `${slug}.mdx`,
        });
        if (lesson?.data?.lesson) {
            return { props: { source: "lesson", ...lesson } };
        }
    }
    catch {
        // Fall through to the normal not-found page.
    }
    return { notFound: true };
};

    async function __Next_Translate__getStaticProps__1a0fc0d55b9__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/openings/[slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0fc0d55b9__ as getStaticProps }
  